/**
 * Macro Insights
 * Data-driven recommendations and progress tracking
 */

import type { NutritionDailySummary } from './types';
import { calculateWeeklyChange } from './macro-calculator';

// =============================================================================
// Types
// =============================================================================

export interface WeightEntry {
  date: string; // ISO date
  weight_lbs: number;
}

export interface NutritionLogSummary {
  date: string;
  total_calories: number;
  total_protein_g: number;
  total_carbs_g: number;
  total_fat_g: number;
}

export interface BaselineAnalysis {
  /** Average daily calorie intake */
  average_calories: number;
  /** Average macros */
  average_protein_g: number;
  average_carbs_g: number;
  average_fat_g: number;
  /** Number of days with data */
  days_tracked: number;
  /** Weight change over the period */
  weight_change_lbs: number;
  /** Starting weight */
  start_weight: number;
  /** Ending weight */
  end_weight: number;
  /** Is weight relatively stable? (< 1 lb change per week) */
  is_weight_stable: boolean;
  /** Confidence level based on data quality */
  confidence: 'low' | 'medium' | 'high';
  /** Estimated TDEE based on actual intake and weight change */
  estimated_tdee: number;
  /** Reason for confidence level */
  confidence_reason: string;
}

export interface ProgressAnalysis {
  /** Days since plan started */
  days_on_plan: number;
  /** Expected weight change based on calorie target */
  expected_change_lbs: number;
  /** Actual weight change */
  actual_change_lbs: number;
  /** Difference (positive = ahead of target, negative = behind) */
  variance_lbs: number;
  /** Average adherence to calorie target (percentage) */
  calorie_adherence_percent: number;
  /** Average actual intake */
  average_intake: number;
  /** Target calories */
  target_calories: number;
  /** Status assessment */
  status: 'on_track' | 'ahead' | 'behind' | 'plateau' | 'insufficient_data';
  /** Human-readable insight */
  insight: string;
  /** Recommended action */
  recommendation: ProgressRecommendation | null;
}

export interface ProgressRecommendation {
  type: 'increase_deficit' | 'decrease_deficit' | 'increase_surplus' | 'decrease_surplus' | 'maintain' | 'review_adherence';
  adjustment_calories?: number;
  reason: string;
}

export interface PlateauDetection {
  is_plateau: boolean;
  days_stalled: number;
  threshold_days: number;
  suggestion: string | null;
}

export interface CheckInSummary {
  period_start: string;
  period_end: string;
  average_weight: number;
  weight_trend: 'losing' | 'gaining' | 'stable';
  average_calories: number;
  calorie_target: number;
  adherence_percent: number;
  days_logged: number;
  total_days: number;
  protein_avg: number;
  carbs_avg: number;
  fat_avg: number;
}

// =============================================================================
// Constants
// =============================================================================

/** Minimum days needed for reliable baseline */
const MIN_DAYS_FOR_BASELINE = 5;

/** Ideal days for high confidence baseline */
const IDEAL_DAYS_FOR_BASELINE = 7;

/** Weight stability threshold (lbs per week) */
const WEIGHT_STABLE_THRESHOLD = 1.0;

/** Days of no weight change to consider a plateau */
const PLATEAU_THRESHOLD_DAYS = 14;

/** Adherence threshold to consider "on track" */
const GOOD_ADHERENCE_THRESHOLD = 90;

/** Calories per pound of body weight */
const CALORIES_PER_LB = 3500;

// =============================================================================
// Baseline Analysis
// =============================================================================

/**
 * Analyze food logs and weight data to establish baseline TDEE
 * This is more accurate than formulas when we have real data
 */
export function analyzeBaseline(
  nutritionLogs: NutritionLogSummary[],
  weightEntries: WeightEntry[]
): BaselineAnalysis | null {
  if (nutritionLogs.length < MIN_DAYS_FOR_BASELINE) {
    return null;
  }

  // Calculate average intake
  const totalCalories = nutritionLogs.reduce((sum, log) => sum + log.total_calories, 0);
  const totalProtein = nutritionLogs.reduce((sum, log) => sum + log.total_protein_g, 0);
  const totalCarbs = nutritionLogs.reduce((sum, log) => sum + log.total_carbs_g, 0);
  const totalFat = nutritionLogs.reduce((sum, log) => sum + log.total_fat_g, 0);

  const average_calories = Math.round(totalCalories / nutritionLogs.length);
  const average_protein_g = Math.round(totalProtein / nutritionLogs.length);
  const average_carbs_g = Math.round(totalCarbs / nutritionLogs.length);
  const average_fat_g = Math.round(totalFat / nutritionLogs.length);

  // Sort weight entries by date
  const sortedWeights = [...weightEntries].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Get weight change
  let start_weight = sortedWeights[0]?.weight_lbs || 0;
  let end_weight = sortedWeights[sortedWeights.length - 1]?.weight_lbs || start_weight;
  const weight_change_lbs = end_weight - start_weight;

  // Calculate weeks elapsed
  const firstDate = new Date(nutritionLogs[0].date);
  const lastDate = new Date(nutritionLogs[nutritionLogs.length - 1].date);
  const weeksElapsed = Math.max(1, (lastDate.getTime() - firstDate.getTime()) / (7 * 24 * 60 * 60 * 1000));

  // Is weight stable?
  const weeklyChange = weight_change_lbs / weeksElapsed;
  const is_weight_stable = Math.abs(weeklyChange) <= WEIGHT_STABLE_THRESHOLD;

  // Estimate TDEE based on intake and weight change
  // If weight stable: TDEE ≈ average intake
  // If weight changing: TDEE = average intake - (weight_change * 3500 / days)
  const days = nutritionLogs.length;
  const caloriesFromWeightChange = (weight_change_lbs * CALORIES_PER_LB) / days;
  const estimated_tdee = Math.round(average_calories - caloriesFromWeightChange);

  // Determine confidence
  let confidence: 'low' | 'medium' | 'high';
  let confidence_reason: string;

  if (nutritionLogs.length >= IDEAL_DAYS_FOR_BASELINE && sortedWeights.length >= 2 && is_weight_stable) {
    confidence = 'high';
    confidence_reason = `${nutritionLogs.length} days tracked with stable weight - reliable baseline`;
  } else if (nutritionLogs.length >= MIN_DAYS_FOR_BASELINE && sortedWeights.length >= 2) {
    confidence = 'medium';
    confidence_reason = `${nutritionLogs.length} days tracked - moderate confidence`;
  } else {
    confidence = 'low';
    confidence_reason = 'Limited data - consider tracking more days';
  }

  return {
    average_calories,
    average_protein_g,
    average_carbs_g,
    average_fat_g,
    days_tracked: nutritionLogs.length,
    weight_change_lbs,
    start_weight,
    end_weight,
    is_weight_stable,
    confidence,
    estimated_tdee,
    confidence_reason,
  };
}

// =============================================================================
// Progress Tracking
// =============================================================================

/**
 * Analyze progress since starting a nutrition plan
 */
export function analyzeProgress(
  nutritionLogs: NutritionLogSummary[],
  weightEntries: WeightEntry[],
  target_calories: number,
  calorie_adjustment: number,
  plan_start_date: string
): ProgressAnalysis {
  // Filter to only logs since plan started
  const planStart = new Date(plan_start_date);
  const logsOnPlan = nutritionLogs.filter(log => new Date(log.date) >= planStart);
  const weightsOnPlan = weightEntries.filter(w => new Date(w.date) >= planStart);

  const days_on_plan = logsOnPlan.length;

  if (days_on_plan < 3) {
    return {
      days_on_plan,
      expected_change_lbs: 0,
      actual_change_lbs: 0,
      variance_lbs: 0,
      calorie_adherence_percent: 0,
      average_intake: 0,
      target_calories,
      status: 'insufficient_data',
      insight: 'Keep logging! Need at least 3 days of data for insights.',
      recommendation: null,
    };
  }

  // Calculate expected change based on deficit/surplus
  const weeks_on_plan = days_on_plan / 7;
  const expected_change_lbs = calculateWeeklyChange(calorie_adjustment) * weeks_on_plan;

  // Calculate actual weight change
  const sortedWeights = [...weightsOnPlan].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const actual_change_lbs = sortedWeights.length >= 2
    ? sortedWeights[sortedWeights.length - 1].weight_lbs - sortedWeights[0].weight_lbs
    : 0;

  const variance_lbs = actual_change_lbs - expected_change_lbs;

  // Calculate adherence
  const totalIntake = logsOnPlan.reduce((sum, log) => sum + log.total_calories, 0);
  const average_intake = Math.round(totalIntake / logsOnPlan.length);
  const calorie_adherence_percent = Math.round((1 - Math.abs(average_intake - target_calories) / target_calories) * 100);

  // Determine status
  let status: ProgressAnalysis['status'];
  let insight: string;
  let recommendation: ProgressRecommendation | null = null;

  const varianceThreshold = 0.5; // lbs

  if (days_on_plan < 14) {
    // Too early to make strong assessments
    status = 'on_track';
    insight = `${days_on_plan} days in. Stay consistent - meaningful trends emerge after 2 weeks.`;
  } else if (Math.abs(variance_lbs) < varianceThreshold) {
    status = 'on_track';
    insight = `Right on target! ${calorie_adjustment < 0 ? 'Deficit' : 'Surplus'} is working as expected.`;
  } else if (calorie_adjustment < 0) {
    // Fat loss goal
    if (actual_change_lbs < expected_change_lbs - varianceThreshold) {
      // Losing faster than expected
      status = 'ahead';
      insight = `Losing faster than expected (${Math.abs(actual_change_lbs).toFixed(1)} vs ${Math.abs(expected_change_lbs).toFixed(1)} lbs).`;
      if (actual_change_lbs < -2 * weeks_on_plan) {
        recommendation = {
          type: 'decrease_deficit',
          adjustment_calories: 100,
          reason: 'Rapid loss may lead to muscle loss. Consider a smaller deficit.',
        };
      }
    } else if (actual_change_lbs > expected_change_lbs + varianceThreshold) {
      // Losing slower or not at all
      if (actual_change_lbs >= -0.1 && days_on_plan >= PLATEAU_THRESHOLD_DAYS) {
        status = 'plateau';
        insight = `Weight stalled for ${days_on_plan} days despite ${average_intake} cal average.`;
        recommendation = calorie_adherence_percent < GOOD_ADHERENCE_THRESHOLD
          ? {
              type: 'review_adherence',
              reason: `Adherence at ${calorie_adherence_percent}%. Focus on hitting target consistently before adjusting.`,
            }
          : {
              type: 'increase_deficit',
              adjustment_calories: -100,
              reason: 'Good adherence but stalled. Metabolic adaptation may require a small adjustment.',
            };
      } else {
        status = 'behind';
        insight = `Progress slower than expected. Lost ${Math.abs(actual_change_lbs).toFixed(1)} lbs vs expected ${Math.abs(expected_change_lbs).toFixed(1)} lbs.`;
        recommendation = calorie_adherence_percent < GOOD_ADHERENCE_THRESHOLD
          ? {
              type: 'review_adherence',
              reason: `Adherence at ${calorie_adherence_percent}%. Tighten up tracking before adjusting calories.`,
            }
          : {
              type: 'increase_deficit',
              adjustment_calories: -100,
              reason: 'Consider a slightly larger deficit to hit your target rate.',
            };
      }
    }
  } else if (calorie_adjustment > 0) {
    // Muscle gain goal
    if (actual_change_lbs > expected_change_lbs + varianceThreshold) {
      status = 'ahead';
      insight = `Gaining faster than expected (${actual_change_lbs.toFixed(1)} vs ${expected_change_lbs.toFixed(1)} lbs).`;
      if (actual_change_lbs > 1 * weeks_on_plan) {
        recommendation = {
          type: 'decrease_surplus',
          adjustment_calories: -100,
          reason: 'Gaining faster than optimal for lean gains. Consider reducing surplus.',
        };
      }
    } else if (actual_change_lbs < expected_change_lbs - varianceThreshold) {
      status = 'behind';
      insight = `Gaining slower than expected. Consider if calories are enough.`;
      recommendation = {
        type: 'increase_surplus',
        adjustment_calories: 100,
        reason: 'May need more calories to support growth goal.',
      };
    }
  } else {
    status = 'on_track';
    insight = 'Maintaining as expected.';
  }

  return {
    days_on_plan,
    expected_change_lbs,
    actual_change_lbs,
    variance_lbs,
    calorie_adherence_percent,
    average_intake,
    target_calories,
    status,
    insight,
    recommendation,
  };
}

// =============================================================================
// Plateau Detection
// =============================================================================

/**
 * Detect if client is in a weight loss plateau
 */
export function detectPlateau(
  weightEntries: WeightEntry[],
  calorie_adjustment: number
): PlateauDetection {
  if (weightEntries.length < 7) {
    return {
      is_plateau: false,
      days_stalled: 0,
      threshold_days: PLATEAU_THRESHOLD_DAYS,
      suggestion: null,
    };
  }

  // Only check for plateau if in a deficit
  if (calorie_adjustment >= 0) {
    return {
      is_plateau: false,
      days_stalled: 0,
      threshold_days: PLATEAU_THRESHOLD_DAYS,
      suggestion: null,
    };
  }

  // Sort by date descending (most recent first)
  const sorted = [...weightEntries].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Get recent 2-week average
  const recentWeights = sorted.slice(0, 14);
  if (recentWeights.length < 7) {
    return {
      is_plateau: false,
      days_stalled: 0,
      threshold_days: PLATEAU_THRESHOLD_DAYS,
      suggestion: null,
    };
  }

  const recentAvg = recentWeights.reduce((sum, w) => sum + w.weight_lbs, 0) / recentWeights.length;

  // Compare to previous 2-week average
  const olderWeights = sorted.slice(14, 28);
  if (olderWeights.length < 7) {
    return {
      is_plateau: false,
      days_stalled: 0,
      threshold_days: PLATEAU_THRESHOLD_DAYS,
      suggestion: null,
    };
  }

  const olderAvg = olderWeights.reduce((sum, w) => sum + w.weight_lbs, 0) / olderWeights.length;
  const change = recentAvg - olderAvg;

  // Plateau = less than 0.5 lb change over 2 weeks while in deficit
  const is_plateau = Math.abs(change) < 0.5;

  if (is_plateau) {
    // Calculate how many days since last meaningful drop
    const firstRecent = new Date(sorted[sorted.length - 1].date);
    const lastRecent = new Date(sorted[0].date);
    const days_stalled = Math.round((lastRecent.getTime() - firstRecent.getTime()) / (24 * 60 * 60 * 1000));

    return {
      is_plateau: true,
      days_stalled,
      threshold_days: PLATEAU_THRESHOLD_DAYS,
      suggestion: 'Consider a 100-150 calorie reduction or adding a refeed day to break through.',
    };
  }

  return {
    is_plateau: false,
    days_stalled: 0,
    threshold_days: PLATEAU_THRESHOLD_DAYS,
    suggestion: null,
  };
}

// =============================================================================
// Check-In Summaries
// =============================================================================

/**
 * Generate a weekly or bi-weekly check-in summary
 */
export function generateCheckInSummary(
  nutritionLogs: NutritionLogSummary[],
  weightEntries: WeightEntry[],
  target_calories: number,
  period_days: number = 7
): CheckInSummary | null {
  if (nutritionLogs.length === 0) return null;

  // Get date range
  const endDate = new Date();
  const startDate = new Date(endDate.getTime() - period_days * 24 * 60 * 60 * 1000);

  // Filter logs in range
  const logsInRange = nutritionLogs.filter(log => {
    const date = new Date(log.date);
    return date >= startDate && date <= endDate;
  });

  const weightsInRange = weightEntries.filter(w => {
    const date = new Date(w.date);
    return date >= startDate && date <= endDate;
  });

  if (logsInRange.length === 0) return null;

  // Calculate averages
  const average_calories = Math.round(
    logsInRange.reduce((sum, l) => sum + l.total_calories, 0) / logsInRange.length
  );
  const protein_avg = Math.round(
    logsInRange.reduce((sum, l) => sum + l.total_protein_g, 0) / logsInRange.length
  );
  const carbs_avg = Math.round(
    logsInRange.reduce((sum, l) => sum + l.total_carbs_g, 0) / logsInRange.length
  );
  const fat_avg = Math.round(
    logsInRange.reduce((sum, l) => sum + l.total_fat_g, 0) / logsInRange.length
  );

  // Weight trend
  const average_weight = weightsInRange.length > 0
    ? weightsInRange.reduce((sum, w) => sum + w.weight_lbs, 0) / weightsInRange.length
    : 0;

  let weight_trend: 'losing' | 'gaining' | 'stable' = 'stable';
  if (weightsInRange.length >= 2) {
    const sorted = [...weightsInRange].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    const change = sorted[sorted.length - 1].weight_lbs - sorted[0].weight_lbs;
    if (change < -0.5) weight_trend = 'losing';
    else if (change > 0.5) weight_trend = 'gaining';
  }

  // Adherence
  const adherence_percent = Math.round(
    (1 - Math.abs(average_calories - target_calories) / target_calories) * 100
  );

  return {
    period_start: startDate.toISOString().split('T')[0],
    period_end: endDate.toISOString().split('T')[0],
    average_weight,
    weight_trend,
    average_calories,
    calorie_target: target_calories,
    adherence_percent: Math.max(0, Math.min(100, adherence_percent)),
    days_logged: logsInRange.length,
    total_days: period_days,
    protein_avg,
    carbs_avg,
    fat_avg,
  };
}

// =============================================================================
// Adjustment Suggestions
// =============================================================================

/**
 * Calculate suggested calorie adjustment based on progress
 */
export function suggestAdjustment(
  actual_weekly_loss: number,
  target_weekly_loss: number,
  current_adjustment: number
): { new_adjustment: number; change: number; reason: string } | null {
  const variance = actual_weekly_loss - target_weekly_loss;
  const absVariance = Math.abs(variance);

  // Within acceptable range (0.25 lbs)
  if (absVariance < 0.25) {
    return null;
  }

  // Calculate needed adjustment
  // Each 500 cal deficit ≈ 1 lb/week
  const calories_per_lb_week = 500;
  const adjustment_needed = variance * calories_per_lb_week;

  // Round to nearest 50
  const rounded_adjustment = Math.round(adjustment_needed / 50) * 50;

  if (rounded_adjustment === 0) return null;

  const new_adjustment = current_adjustment + rounded_adjustment;

  return {
    new_adjustment,
    change: rounded_adjustment,
    reason: variance > 0
      ? `Losing ${absVariance.toFixed(1)} lb/week slower than target. ${rounded_adjustment > 0 ? 'Reduce' : 'Increase'} by ${Math.abs(rounded_adjustment)} cal.`
      : `Losing ${absVariance.toFixed(1)} lb/week faster than target. Consider easing up by ${Math.abs(rounded_adjustment)} cal.`,
  };
}
